/**
 * i18n.js — Internationalisation setup for the public site (ES / EN / FR).
 *
 * Why react-i18next:
 *  - Defacto standard for React; supports lazy namespaces, plurals, suspense.
 *  - Plays nicely with React 18+ strict mode.
 *
 * Detection order (set in `detection.order`):
 *   1. URL `?lng=` query  (manual sharing / SEO debugging)
 *   2. localStorage `i18nextLng`  (persists the user's choice across visits)
 *   3. browser `navigator.language`
 *   4. fallback → es
 *
 * We do NOT auto-translate the dynamic CMS content (carta items, blog posts):
 * editorial content is hand-written in Spanish and forcing machine translation
 * would damage SEO and tone. The language switcher therefore impacts UI strings
 * + landing copy that lives in `locales/{lang}.json`.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import es from "./locales/es.json";
import en from "./locales/en.json";
import fr from "./locales/fr.json";

export const SUPPORTED_LANGUAGES = [
    { code: "es", label: "Español", short: "ES" },
    { code: "en", label: "English", short: "EN" },
    { code: "fr", label: "Français", short: "FR" },
];

i18n.use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            es: { translation: es },
            en: { translation: en },
            fr: { translation: fr },
        },
        fallbackLng: "es",
        supportedLngs: ["es", "en", "fr"],
        interpolation: { escapeValue: false },
        detection: {
            order: ["querystring", "localStorage", "navigator"],
            lookupQuerystring: "lng",
            lookupLocalStorage: "i18nextLng",
            caches: ["localStorage"],
        },
        react: { useSuspense: false },
    });

// Keep <html lang> in sync so screen readers + SEO crawlers pick it up.
if (typeof document !== "undefined") {
    document.documentElement.lang = i18n.language?.slice(0, 2) || "es";
    i18n.on("languageChanged", (lng) => {
        document.documentElement.lang = lng.slice(0, 2);
    });
}

export default i18n;
